// Every style for the Fleet Status page. Same Emotion wrapper and theme
// tokens as before — this only moves them out of the container, which had a
// third of its lines in here. Class names stay `fs-` prefixed so these
// selectors cannot cascade into child components.

import styledComponent from '../../utils/styledComponent';
import theme from '../../theme';
import { STRIP_HEIGHT } from './HeartbeatSparkline';

const StyledDiv = styledComponent('div', {
  // No minHeight: the page lives inside a fixed-width .App.container, so pinning it
  // to 100vh sized the grey to the viewport instead of to the content.
  padding: theme.spacing.cardPadding,
  paddingBottom: theme.spacing.sectionGap,
  background: theme.colors.bgPage,

  '& .fs-header': {
    marginBottom: theme.spacing.sectionGap,
  },
  '& .fs-title': {
    ...theme.typography.pageTitle,
    color: theme.colors.textDark,
    margin: 0,
  },
  '& .fs-subtitle': {
    ...theme.typography.body,
    color: theme.colors.textMuted,
    marginTop: theme.spacing.gapSm,
  },

  // Summary stat cards
  '& .fs-summary': {
    display: 'flex',
    gap: theme.spacing.fieldGap,
    marginBottom: theme.spacing.sectionGap,
    flexWrap: 'wrap',
  },
  '& .fs-card': {
    flex: '1 1 0',
    minWidth: theme.sizes.filterFieldMinWidth,
    textAlign: 'left',
    background: theme.colors.bgCard,
    border: theme.borders.card,
    borderRadius: theme.radii.card,
    padding: theme.spacing.bodyPadding,
    cursor: 'pointer',
    transition: theme.transitions.default,
  },
  '& .fs-card:hover': {
    boxShadow: theme.shadows.card,
    transform: 'translateY(-1px)',
  },
  '& .fs-card[aria-pressed="true"]': {
    borderColor: theme.colors.infoBlue,
    boxShadow: `0 0 0 2px ${theme.colors.infoBlue}33`,
  },
  '& .fs-card-count': {
    ...theme.typography.pageTitle,
    margin: 0,
    lineHeight: 1.1,
  },
  '& .fs-card-label': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing.gapMd,
    ...theme.typography.label,
    color: theme.colors.textMuted,
    marginTop: theme.spacing.gapSm,
  },
  // The two absence cards. Narrower rather than shorter, so the row still
  // reads as one band of cards and the health numbers keep the width.
  '& .fs-card--minor': {
    flex: '0.6 1 0',
    minWidth: theme.sizes.summaryCardMinorMinWidth,
  },

  // Top failure-reasons strip
  // A light text toggle, not a full-width section bar: this is help, and it
  // should not carry the same weight as the Heartbeat History data section.
  '& .fs-explainer-toggle': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing.gapSm,
    marginTop: theme.spacing.gapMd,
    padding: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    ...theme.typography.smallButton,
    color: theme.colors.infoBlue,
  },
  '& .fs-explainer-toggle:hover': {
    textDecoration: 'underline',
  },
  '& .fs-explainer-caret': {
    display: 'inline-flex',
    transition: theme.transitions.default,
  },
  '& .fs-explainer-caret svg': {
    width: theme.sizes.iconLg,
    height: theme.sizes.iconLg,
  },
  // Points right when closed, down when open — driven off the button's own
  // aria-expanded, so the icon can never disagree with what it announces.
  '& .fs-explainer-toggle[aria-expanded="false"] .fs-explainer-caret': {
    transform: 'rotate(-90deg)',
  },
  '@media (prefers-reduced-motion: reduce)': {
    '& .fs-explainer-caret': {
      transition: 'none',
    },
    '& .fs-spinner': {
      animation: 'none',
    },
  },
  // One wrapping row of five cells. `auto-fit` collapses the empty tracks, so
  // the five share the width and drop to fewer per row only when a column can
  // no longer hold a readable line — no device breakpoint to maintain.
  '& .fs-explainer': {
    display: 'grid',
    maxWidth: theme.sizes.explainerMaxWidth,
    gridTemplateColumns: `repeat(auto-fit, minmax(min(${theme.sizes.explainerColMinWidth}, 100%), 1fr))`,
    gap: theme.spacing.sectionGap,
    marginTop: theme.spacing.bodyPadding,
    color: theme.colors.text,
  },
  '& .fs-explainer p': {
    margin: 0,
    ...theme.typography.body,
  },
  '& .fs-ex-col': {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.gapMd,
    paddingLeft: theme.spacing.bodyPadding,
    borderLeft: `2px solid ${theme.colors.borderLight}`,
  },
  '& .fs-ex-h': {
    margin: 0,
    ...theme.typography.label,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: theme.colors.textMuted,
  },
  '& .fs-ex-note': {
    color: theme.colors.textMuted,
  },
  '& .fs-summary-note': {
    ...theme.typography.hint,
    color: theme.colors.textMuted,
    margin: `${theme.spacing.gapSm} 0 0`,
  },
  '& .fs-reasons-strip': {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing.gapMd,
    marginBottom: theme.spacing.sectionGap,
  },
  '& .fs-reasons-strip-label': {
    ...theme.typography.label,
    color: theme.colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  '& .fs-reason-chip': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing.gapSm,
    padding: theme.spacing.smallBadgePadding,
    borderRadius: theme.radii.pill,
    ...theme.typography.badge,
    border: theme.borders.card,
    background: theme.colors.bgCard,
    color: theme.colors.textBody,
    cursor: 'pointer',
    transition: theme.transitions.default,
  },
  '& .fs-reason-chip:hover': {
    borderColor: theme.colors.infoBlue,
  },
  '& .fs-reason-chip[aria-pressed="true"]': {
    borderColor: theme.colors.infoBlue,
    background: `${theme.colors.infoBlue}14`,
  },

  // Filter toolbar
  '& .fs-filters': {
    padding: theme.spacing.bodyPadding,
    background: theme.colors.bgCard,
    border: theme.borders.card,
    borderRadius: theme.radii.card,
    marginBottom: theme.spacing.fieldGap,
  },
  // Every applied filter in one place, whichever control set it — the summary
  // cards and reason chips write the same state, so they surface here too.
  '& .fs-applied': {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing.gapSm,
    paddingBottom: theme.spacing.bodyPadding,
    marginBottom: theme.spacing.bodyPadding,
    borderBottom: theme.borders.card,
  },
  '& .fs-applied-inner': {
    display: 'contents',
  },
  // The answer to the chips beside it, so query and result read as one line.
  '& .fs-applied-count': {
    ...theme.typography.badge,
    color: theme.colors.textBody,
    fontVariantNumeric: 'tabular-nums',
  },
  '& .fs-applied-chip': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing.gapSm,
    padding: theme.spacing.smallBadgePadding,
    borderRadius: theme.radii.pill,
    ...theme.typography.badge,
    border: theme.borders.card,
    borderColor: theme.colors.infoBlue,
    background: `${theme.colors.infoBlue}14`,
    color: theme.colors.textBody,
    cursor: 'pointer',
    transition: theme.transitions.default,
  },
  '& .fs-applied-chip:hover': {
    background: `${theme.colors.infoBlue}29`,
  },
  '& .fs-applied-x': {
    color: theme.colors.textMuted,
  },
  '& .fs-sr-only': {
    position: 'absolute',
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
  },
  // Its own row above the facet groups: it picks out named fridges rather than
  // narrowing by an attribute, and it is the field people paste a list into.
  '& .fs-search-row': {
    display: 'flex',
    paddingBottom: theme.spacing.bodyPadding,
    marginBottom: theme.spacing.bodyPadding,
    borderBottom: theme.borders.card,
  },
  '& .fs-search-row .fs-field': {
    flex: 1,
    maxWidth: theme.sizes.searchFieldMaxWidth,
  },
  '& .fs-group': {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.inputLabelGap,
  },
  '& .fs-group + .fs-group': {
    marginTop: theme.spacing.bodyPadding,
  },
  '& .fs-group-label': {
    ...theme.typography.hint,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textMuted,
  },
  '& .fs-toolbar': {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    gap: theme.spacing.fieldGap,
  },
  '& .fs-field': {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.inputLabelGap,
  },
  '& .fs-field-label': {
    ...theme.typography.hint,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: theme.colors.textMuted,
  },
  // A label whose name does not fully say what it filters on, and so carries a
  // definition on hover. The dotted underline is what says one is there.
  '& .fs-field-label--defined': {
    textDecoration: 'underline dotted',
    textUnderlineOffset: '3px',
    cursor: 'help',
  },
  '& .fs-control': {
    padding: theme.spacing.inputPadding,
    minHeight: theme.sizes.inputMinHeight,
    minWidth: '150px',
    border: theme.borders.input,
    borderRadius: theme.radii.input,
    ...theme.typography.body,
    color: theme.colors.textBody,
    background: theme.colors.bgCard,
    transition: theme.transitions.default,
  },
  '& .fs-control:hover': {
    borderColor: theme.colors.infoBlue,
  },
  '& .fs-control:focus': {
    outline: 'none',
    borderColor: theme.colors.infoBlue,
    boxShadow: `0 0 0 2px ${theme.colors.infoBlue}33`,
  },
  '& .fs-clear': {
    ...theme.typography.smallButton,
    background: 'none',
    border: 'none',
    color: theme.colors.infoBlue,
    cursor: 'pointer',
    padding: theme.spacing.smallButtonPadding,
  },

  // Incomplete-read notice — "small fleet" vs "we can't see part of the fleet".
  '& .fs-notice': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.gapMd,
    padding: theme.spacing.bodyPadding,
    marginBottom: theme.spacing.fieldGap,
    borderRadius: theme.radii.card,
    ...theme.typography.body,
    background: theme.colors.warningCalloutBg,
    border: `1px solid ${theme.colors.warningCalloutBorder}`,
    color: theme.colors.warningCalloutText,
  },
  '& .fs-notice--error': {
    background: theme.colors.dangerBg,
    borderColor: theme.colors.dangerBorder,
    color: theme.colors.danger,
  },

  // Meta row (updated / count / refresh)
  '& .fs-meta': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.gapXl,
    marginBottom: theme.spacing.fieldGap,
    color: theme.colors.textMuted,
    ...theme.typography.hint,
  },
  '& .fs-meta-spacer': {
    flex: 1,
  },
  // Composition of the visible rows; the dot ties each count to its status pill.
  '& .fs-meta-chip': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing.gapSm,
  },
  '& .fs-csv': {
    padding: theme.spacing.smallButtonPadding,
    ...theme.typography.smallButton,
    color: theme.colors.infoBlue,
    background: theme.colors.bgCard,
    border: `1px solid ${theme.colors.infoBlue}55`,
    borderRadius: theme.radii.input,
    cursor: 'pointer',
    marginRight: theme.spacing.gapMd,
    transition: theme.transitions.default,
  },
  '& .fs-csv:hover': {
    background: `${theme.colors.infoBlue}14`,
  },
  '& .fs-csv:disabled': {
    color: theme.colors.disabled,
    borderColor: theme.colors.borderLight,
    cursor: 'not-allowed',
    background: theme.colors.bgCard,
  },
  '& .fs-refresh': {
    padding: theme.spacing.smallButtonPadding,
    ...theme.typography.smallButton,
    color: theme.colors.white,
    background: theme.colors.primary,
    border: 'none',
    borderRadius: theme.radii.input,
    cursor: 'pointer',
    transition: theme.transitions.default,
  },
  '& .fs-refresh:hover': {
    background: theme.colors.primaryHover,
  },
  '& .fs-refresh:disabled': {
    background: theme.colors.disabled,
    cursor: 'not-allowed',
  },

  // Table
  '& .fs-tablewrap': {
    border: theme.borders.card,
    borderRadius: theme.radii.card,
    overflowX: 'auto',
    maxWidth: '100%',
    background: theme.colors.bgCard,
    // Keeps the last row off the bottom edge of the screen.
    marginBottom: theme.spacing.sectionGap,
  },
  // The min width is what makes `.fs-tablewrap`'s overflow reachable: at 100%
  // alone the table could never exceed its container, so a narrow viewport
  // squeezed columns instead of scrolling.
  // `separate` rather than `collapse` so the frozen first column keeps its
  // borders — collapsed borders belong to the table, not the cell, and drop out
  // from under a sticky one. Nothing here sets a top or side border, so with
  // zero spacing the two modes render alike.
  '& .fs-table': {
    width: '100%',
    minWidth: theme.sizes.fleetTableMinWidth,
    borderCollapse: 'separate',
    borderSpacing: 0,
  },
  '& .fs-table thead th': {
    background: theme.colors.bgMuted,
    fontSize: theme.sizes.smallFontSize,
    fontWeight: theme.fontWeights.semibold,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: theme.colors.textMuted,
    textAlign: 'left',
    padding: theme.spacing.countInputPadding,
    borderBottom: theme.borders.card,
  },
  // Wraps at spaces and commas only. Breaking anywhere used to split every cell
  // mid-token ("STATUS_FF_DOOR_OPE / N"), so cells that genuinely need it — free
  // text out of eMaint — opt in themselves. See `.fs-wo-desc`.
  '& .fs-table td': {
    padding: theme.spacing.countInputPadding,
    borderBottom: `1px solid ${theme.colors.borderLight}`,
    fontSize: theme.sizes.smallFontSize,
    color: theme.colors.textBody,
    verticalAlign: 'top',
  },
  // Which fridge a row describes has to survive scrolling to the columns that
  // say anything about it, so Location stays put while the other twelve pass
  // underneath. The header cell already has a background; body cells are
  // transparent, and a sticky transparent cell shows the scrolled columns
  // through itself.
  '& .fs-table th:first-child, & .fs-table td:first-child': {
    position: 'sticky',
    left: 0,
    zIndex: 1,
    borderRight: `1px solid ${theme.colors.borderLight}`,
  },
  '& .fs-table tbody td:first-child': {
    background: theme.colors.bgCard,
  },
  '& .fs-table tbody tr': {
    transition: theme.transitions.default,
  },
  '& .fs-table tbody tr:hover': {
    background: theme.colors.bgMuted,
  },
  // The row's own hover colour cannot reach an opaque sticky cell.
  '& .fs-table tbody tr:hover td:first-child': {
    background: theme.colors.bgMuted,
  },
  '& .fs-table tbody tr:last-child td': {
    borderBottom: 'none',
  },
  '& .fs-sort': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing.gapSm,
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    font: 'inherit',
    letterSpacing: 'inherit',
    textTransform: 'inherit',
    color: 'inherit',
  },
  // Sortability cue: muted ↕ on every sortable header, solid ▲/▼ when active.
  '& .fs-sort-arrow': {
    fontSize: theme.sizes.smallFontSize,
    color: theme.colors.textHint,
    lineHeight: 1,
  },
  '& .fs-sort:hover .fs-sort-arrow': {
    color: theme.colors.textMuted,
  },
  '& .fs-sort-arrow--active': {
    color: theme.colors.infoBlue,
  },
  '& .fs-loc': {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing.gapMd,
  },
  // Still reads as the row heading; the resets only strip the UA's button chrome.
  '& .fs-loc-id': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing.gapSm,
    padding: 0,
    border: 'none',
    background: 'none',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
    fontWeight: theme.fontWeights.medium,
    color: theme.colors.textDark,
    borderRadius: theme.radii.badge,
  },
  '& .fs-loc-id:focus-visible': {
    outline: 'none',
    boxShadow: `0 0 0 2px ${theme.colors.infoBlue}33`,
  },
  // Faded rather than unmounted, so revealing it never reflows the cell.
  '& .fs-loc-copy': {
    display: 'inline-flex',
    opacity: 0,
    color: theme.colors.textMuted,
    transition: theme.transitions.default,
  },
  '& .fs-loc-id:hover .fs-loc-copy': {
    opacity: 1,
  },
  '& .fs-loc-id:focus-visible .fs-loc-copy': {
    opacity: 1,
  },
  '& .fs-loc-id--copied .fs-loc-copy': {
    opacity: 1,
    color: theme.colors.primary,
  },
  '& .fs-loc-name': {
    fontSize: theme.sizes.smallFontSize,
    fontWeight: theme.fontWeights.normal,
    fontStyle: 'italic',
    color: theme.colors.textMuted,
  },
  // Wraps: unwrapped, the four nowrap buttons were one ~300px unbreakable line,
  // and that floor — not the 11% colgroup width — is what sized the column.
  '& .fs-loc-actions': {
    display: 'inline-flex',
    flexWrap: 'wrap',
    gap: theme.spacing.gapSm,
  },
  '& .fs-minibtn': {
    display: 'inline-flex',
    alignItems: 'center',
    padding: theme.spacing.smallBadgePadding,
    borderRadius: theme.radii.badge,
    ...theme.typography.badge,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    border: `1px solid ${theme.colors.infoBlue}55`,
    color: theme.colors.infoBlue,
    background: theme.colors.bgCard,
    transition: theme.transitions.default,
  },
  '& .fs-minibtn:hover': {
    background: `${theme.colors.infoBlue}14`,
  },
  '& .fs-minibtn--timeline': {
    borderColor: `${theme.colors.primary}55`,
    color: theme.colors.primary,
  },
  '& .fs-minibtn--timeline:hover': {
    background: `${theme.colors.primary}14`,
  },
  '& .fs-minibtn--detail': {
    borderColor: `${theme.colors.accentAmber}55`,
    color: theme.colors.accentAmber,
  },
  '& .fs-minibtn--detail:hover': {
    background: `${theme.colors.accentAmber}14`,
  },
  '& .fs-minibtn--inventory': {
    borderColor: `${theme.colors.accentPurple}55`,
    color: theme.colors.accentPurple,
  },
  '& .fs-minibtn--inventory:hover': {
    background: `${theme.colors.accentPurple}14`,
  },

  // Status pill + chips
  // A status is a label from a fixed set, never a phrase to wrap.
  '& .fs-pill': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing.gapMd,
    padding: theme.spacing.pillPadding,
    borderRadius: theme.radii.pill,
    ...theme.typography.badge,
    fontWeight: theme.fontWeights.semibold,
    whiteSpace: 'nowrap',
  },
  '& .fs-dot': {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    display: 'inline-block',
  },
  '& .fs-chip': {
    display: 'inline-block',
    padding: theme.spacing.smallBadgePadding,
    marginRight: theme.spacing.gapSm,
    borderRadius: theme.radii.badge,
    ...theme.typography.badge,
    background: `${theme.colors.danger}1f`,
    color: theme.colors.danger,
  },
  // Wider than a select: it holds two date inputs plus the preset chips.
  // Sized like the other filter fields, not for a date range stacked inside
  // it — the range now sits alongside the dropdown and widens the field only
  // while it is showing.
  '& .fs-online-filter': {
    minWidth: theme.sizes.filterFieldMinWidth,
  },
  // Wraps rather than overflowing: on a narrow viewport the range drops under
  // the dropdown, which is the old layout and still readable.
  '& .fs-online-controls': {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: theme.spacing.fieldGap,
  },
  '& .fs-online': {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing.gapSm,
  },
  // Deliberately not `fs-chip`: that one is red, and "not installed yet"
  // explains an alarm rather than being one.
  '& .fs-install-chip': {
    display: 'inline-block',
    padding: theme.spacing.smallBadgePadding,
    borderRadius: theme.radii.badge,
    ...theme.typography.badge,
  },
  // Blue = not live yet; violet = live but new. Two tones so a scan separates
  // "nothing to see here" from "in its first weeks".
  '& .fs-install-chip--upcoming': {
    background: theme.colors.badgePm,
    color: theme.colors.badgePmText,
  },
  '& .fs-install-chip--new': {
    background: theme.colors.badgeInstalled,
    color: theme.colors.badgeInstalledText,
  },
  '& .fs-wos': {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.gapMd,
  },
  '& .fs-wo-item': {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.gapXs,
  },
  '& .fs-wo-top': {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing.gapSm,
  },
  '& .fs-wo-age': {
    ...theme.typography.hint,
    color: theme.colors.textMuted,
    whiteSpace: 'nowrap',
  },
  // Wraps rather than truncating: a work order nobody can read is no use, and
  // these cells are already the tallest thing in the row.
  '& .fs-wo-desc': {
    ...theme.typography.hint,
    color: theme.colors.textMuted,
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
  },
  '& .fs-wo': {
    ...theme.typography.badge,
    color: theme.colors.infoBlue,
    textDecoration: 'none',
  },
  '& .fs-wo:hover': {
    textDecoration: 'underline',
  },
  '& .fs-ok': {
    ...theme.typography.badge,
    color: theme.colors.textMuted,
  },
  // "we have no reading", visually distinct from "All OK" so absence of data
  // never reads as a clean bill of health.
  '& .fs-unknown': {
    ...theme.typography.badge,
    color: theme.colors.textHint,
  },
  '& .fs-reasons': {
    color: theme.colors.textMuted,
    ...theme.typography.hint,
    whiteSpace: 'normal',
    maxWidth: theme.sizes.fleetReasonsMaxWidth,
    display: 'inline-block',
  },
  '& .fs-loadmore': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.gapMd,
    padding: theme.spacing.bodyPadding,
    borderTop: `1px solid ${theme.colors.borderLight}`,
    ...theme.typography.hint,
    color: theme.colors.textMuted,
  },
  // Sits beside the buttons rather than replacing the bar, so nothing reflows
  // at the moment the tab is about to stop responding.
  '& .fs-expanding': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing.gapSm,
    color: theme.colors.textMuted,
  },
  // Deliberately not the Loader atom: that one is a 28px block with 60px
  // margins meant to stand in for a whole page, and this has to fit on a row.
  '& .fs-spinner': {
    display: 'inline-block',
    width: theme.sizes.iconLg,
    height: theme.sizes.iconLg,
    border: `2px solid ${theme.colors.borderLight}`,
    borderTopColor: theme.colors.infoBlue,
    borderRadius: '50%',
    animation: 'fs-expand-spin 0.7s linear infinite',
  },
  '@keyframes fs-expand-spin': {
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' },
  },
  '& .fs-empty': {
    padding: theme.spacing.emptyStatePadding,
    textAlign: 'center',
    color: theme.colors.textMuted,
  },

  // 24h sparkline — slim strip stacked under the relative time.
  '& .fs-hb': {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.gapSm,
  },
  '& .fs-hb-latest-label': {
    color: theme.colors.textMuted,
    fontWeight: theme.fontWeights.medium,
  },
  // Flags a card that's fallen behind the 15-min cron, so it isn't read as live.
  '& .fs-hb-stale': {
    ...theme.typography.badge,
    color: theme.colors.warningText,
  },
  // Tabular numerals so a sorted column scans as magnitudes, not ragged text.
  '& .fs-dropouts': {
    fontVariantNumeric: 'tabular-nums',
    color: theme.colors.textMuted,
  },
  '& .fs-dropouts--bad': {
    color: theme.colors.danger,
    fontWeight: theme.fontWeights.semibold,
  },
  '& .fs-spark': {
    position: 'relative',
    width: '100%',
  },
  // "24h ago … now" endpoints under the strip, so the time axis is explicit.
  '& .fs-spark-scale': {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '2px',
    ...theme.typography.hint,
    color: theme.colors.textHint,
  },
  '& .fs-spark-canvas': {
    display: 'block',
    width: '100%',
    height: `${STRIP_HEIGHT}px`,
  },
  // Placeholder for a strip that's scrolled out of view and not painted yet.
  '& .fs-spark-idle': {
    width: '100%',
    height: `${STRIP_HEIGHT}px`,
    borderRadius: theme.radii.badge,
    background: theme.colors.bgMuted,
  },
  '& .fs-spark-tip': {
    position: 'absolute',
    bottom: `calc(100% + ${theme.spacing.gapSm})`,
    transform: 'translateX(-50%)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing.gapSm,
    padding: theme.spacing.smallBadgePadding,
    borderRadius: theme.radii.badge,
    background: theme.colors.textDark,
    color: theme.colors.white,
    ...theme.typography.badge,
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
    zIndex: 2,
  },
  '& .fs-spark-tip-dot': {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
  },

  // Phone: the frozen Location column stays frozen, but stops being allowed to
  // grow to whatever its widest row needs.
  [`@media (max-width: ${theme.breakpoints.mobile})`]: {
    // The cap goes on the cell's inner div, not the `td`: max-width on a table
    // cell is ignored under `table-layout: auto`, where content sizes the column.
    '& .fs-loc': {
      maxWidth: theme.sizes.fleetStickyColMaxWidth,
    },
    // The last unbreakable thing in the cell. Underscores are not line-break
    // opportunities, so an id like DE_WIL_NEMOURS_CHILDRENS_LEFT is one token
    // and held the column open on its own. Desktop keeps ids unbroken.
    '& .fs-loc-id': {
      overflowWrap: 'anywhere',
    },
  },
});

export default StyledDiv;
